import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { deleteBatch, fetchBatchImage, listBatches } from '../api'
import type { IdeaBatch } from '../types'

export default function HistoryPage() {
  const [batches, setBatches] = useState<IdeaBatch[]>([])
  const [thumbs, setThumbs] = useState<Record<string, (string | null)[]>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [deletedMsg, setDeletedMsg] = useState('')
  const urlsRef = useState<string[]>(() => [])[0]

  const refresh = () => {
    setLoading(true)
    listBatches()
      .then((list) => {
        setBatches(list)
        // 仅 CSS 缩放展示，使用完整 JPEG，不另存缩略图
        list.forEach(async (b) => {
          if (b.imageStatus === 'none') return
          const arr: (string | null)[] = [null, null, null]
          for (let i = 0; i < 3; i++) {
            const url = await fetchBatchImage(b.id, i)
            if (url) urlsRef.push(url)
            arr[i] = url
          }
          setThumbs((m) => ({ ...m, [b.id]: arr }))
        })
      })
      .catch((err) => setError(err instanceof Error ? err.message : '加载失败'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    refresh()
    return () => {
      urlsRef.forEach((u) => URL.revokeObjectURL(u))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function onDelete(e: React.MouseEvent, id: string) {
    e.preventDefault()
    e.stopPropagation()
    if (!window.confirm('删除这条历史（含概念图）？此操作不可恢复。')) return
    try {
      await deleteBatch(id)
      setBatches((list) => list.filter((b) => b.id !== id))
      setDeletedMsg((m) => {
        const n = (m.match(/\d+/)?.[0] ? Number(m.match(/\d+/)?.[0]) : 0) + 1
        return `已删除 ${n} 条记录`
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : '删除失败')
    }
  }

  return (
    <section className="section">
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
        <h2 style={{ margin: 0 }}>HISTORY</h2>
        <Link className="btn btn-primary" to="/create">
          新建创意
        </Link>
      </div>

      {loading ? (
        <div className="loading-row">
          <span className="spinner" />
          读取历史…
        </div>
      ) : null}
      {error ? <div className="alert alert-error">{error}</div> : null}
      {deletedMsg ? <div className="alert alert-info">{deletedMsg}</div> : null}

      {!loading && !error && batches.length === 0 ? (
        <div className="empty">
          <p>还没有生成批次</p>
          <Link className="btn btn-primary" to="/create">
            生成第一批 3 个创意
          </Link>
        </div>
      ) : null}

      <div className="list">
        {batches.map((b) => (
          <div key={b.id} className="list-item list-item-stack">
            <Link to={`/result/${b.id}`} className="list-item-main">
              <div>
                <h3>{b.ideas.map((i) => i.name).join(' / ')}</h3>
                <div className="meta">
                  <span>{b.input.slice(0, 80)}{b.input.length > 80 ? '…' : ''}</span>
                  <span className="chip">{new Date(b.createdAt).toLocaleString('zh-CN')}</span>
                  <span className="chip">
                    {b.imageStatus === 'ok' ? '含展示图' : b.imageStatus === 'partial' ? '部分出图' : b.imageStatus === 'failed' ? '出图失败' : '出图中'}
                  </span>
                </div>
              </div>
            </Link>
            {(thumbs[b.id] || []).some(Boolean) ? (
              <div className="thumb-row" aria-label="本条三张概念图预览">
                {(thumbs[b.id] || []).map((u, i) =>
                  u ? (
                    <img key={i} src={u} alt={`方案 ${String.fromCharCode(65 + i)} 预览`} />
                  ) : (
                    <div key={i} className="thumb-empty">
                      {String.fromCharCode(65 + i)}
                    </div>
                  ),
                )}
              </div>
            ) : null}
            <button type="button" className="btn btn-ghost btn-sm list-del" onClick={(e) => onDelete(e, b.id)}>
              删除
            </button>
          </div>
        ))}
      </div>
    </section>
  )
}
